<template>
	<div class="preview-player">
		<Player
			v-if="hlsStreamAvailable && hlsUrl"
			v-show="isInitialized"
			ref="player"
			class="preview-player__player"
			:class="{ '-playing': isPlaying }"
			:hls-url="hlsUrl"
			:show-controls="false"
			:pointer-events="false"
			:show-errors="false"
			:loading-spinner-enabled="false"
			:hls-config="{ maxBufferSize: 4000000 }"
			@quality-levels-updated="onQualityLevelsUpdated"
			@is-playing-change="onIsPlayingChange"
			@timeupdate="handleTimeUpdate"
			@loadedmetadata="handleLoadedMetadata"
		/>
	</div>
</template>

<script>
import Player from '@shared/components/Player.vue';
import laroute from '@laroute';

export default {
	name:       'PreviewPlayer',
	components: { Player },
	props:      {
		eventId: {
			type:     Number,
			required: true,
		},
		playPreview: {
			type:    Boolean,
			default: false,
		},
	},
	data() {
		return {
			isPlaying:          false,
			isInitialized:      false,
			hlsStreamAvailable: false,
			hlsLoaded:          false,
			hlsUrl:             '',
			playerQualityLevel: null,
			elapsed:            0,
			videoDuration:      0,
		};
	},
	watch: {
		playPreview(newValue) {
			if (newValue) {
				if (!this.isInitialized) {
					this.initPlayer();
				} else {
					this.play();
				}
			} else {
				this.stopPlayer();
			}
		},
	},
	methods: {
		onIsPlayingChange(isPlaying) {
			this.isPlaying = isPlaying;
			if (isPlaying) {
				this.isInitialized = true;
			}
			this.$emit('is-playing-change', isPlaying);
		},
		async initPlayer() {
			await this.loadHlsUrl();

			if (this.$refs.player) {
				this.$refs.player.mute();
				if (this.playPreview) {
					this.$refs.player.play();
				}
			}
		},
		async loadHlsUrl() {
			if (this.hlsLoaded) {
				return;
			}
			try {
				const options = {
					method: 'GET',
					url:    laroute.route('api.event.playout', { event: this.eventId }),
				};

				const response = await fetch(options.url, options);
				const data = await response.json();
				const hlsUrl = data?.hlsUrl;

				if (hlsUrl && !data.isPreview) { // We don't show the stream preview in the preview player
					this.hlsUrl = hlsUrl;
					this.hlsStreamAvailable = true;
					this.$emit('hls-stream-available', true);
				} else {
					this.hlsStreamAvailable = false;
				}
			} catch (error) {
				this.hlsStreamAvailable = false;
			} finally {
				this.hlsLoaded = true;
			}
		},
		onQualityLevelsUpdated(levels) {
			const index360p = levels.findIndex((level) => level.height === 360);
			if (index360p !== -1 && index360p !== this.playerQualityLevel) {
				this.$refs.player.setQuality(index360p);
				this.playerQualityLevel = index360p;
			}
		},
		handleTimeUpdate(time) {
			this.elapsed = time;
			this.$emit('timeupdate', time);
		},
		handleLoadedMetadata() {
			this.videoDuration = this.$refs.player.getDuration();
			this.$emit('loadedmetadata', this.videoDuration);
		},
		stopPlayer() {
			if (this.$refs.player) {
				this.$refs.player.pause();
			}
		},
		setVideoTime(time) {
			this.elapsed = time;
			if (this.$refs.player) {
				this.$refs.player.seek(time);
			}
		},
		// Public methods for external control
		play() {
			if (this.$refs.player) {
				this.$refs.player.play();
			}
		},
		stop() {
			this.stopPlayer();
		},
		seek(time) {
			this.setVideoTime(time);
		},
		// Getters for external access
		getElapsed() {
			return this.elapsed;
		},
		getDuration() {
			return this.videoDuration;
		},
	},
};
</script>

<style lang="scss" scoped>
@import '@shared/sass/shared-variables';

.preview-player {
	position: relative;
	width: 100%;
	height: 100%;
}

.preview-player__player {
	position: absolute;
	top: 0;
	left: 0;
	width: 100%;
	height: 100%;
}
</style>
